import * as models from 'smg/customer-api/models';
import { ShowError } from 'smg/errors';
import { ReportValidity } from 'smg/form';
import * as template from 'smg/template';

import { UpdateSmartPayWarning } from '../smartpay';
import { State } from '../state';
import { Payment } from '../types';
import { CapitalizeFirstLetter } from '../util';

import { InitCCInputs, InitPaymentTypeSelect } from './inputs';
import { CreatePaymentOption, DeleteSavedPaymentOption, PaymentOptions, PendingPaymentOptionID } from './options';
import { ApplyCardIssuer } from './plan';
import { EditSavedPaymentOption, PromptExistingPaymentOption, PromptNewPaymentOption, PromptNewPaymentOptionByType } from './prompts';
import { DrawSplit, MaybeMountSwitch, SetupSplit } from './split';
import { UpdatePaymentSummary } from './summary';

// SetupPayment wires every payment-related callable onto state, then binds
// the payment-accordion toggle.
export function SetupPayment(state: State): void {
    state.initPayment = () => initPayment(state);
    state.updatePaymentSummary = () => UpdatePaymentSummary(state);
    state.getPaymentOptions = page => PaymentOptions(page);
    state.createPaymentOption = () => CreatePaymentOption(state);
    state.deleteSavedPaymentOption = poID => DeleteSavedPaymentOption(state, poID);
    state.editSavedPaymentOption = (poID, returnTo) => EditSavedPaymentOption(state, poID, returnTo);
    state.promptNewPaymentOption = ($el, returnTo) => PromptNewPaymentOption(state, $el, returnTo);
    state.promptExistingPaymentOption = ($el, returnTo) => PromptExistingPaymentOption(state, $el, returnTo);
    state.promptNewPaymentOptionByType = (typeID, $el, returnTo, uploadIDs) =>
        PromptNewPaymentOptionByType(state, typeID, $el, returnTo, uploadIDs);
    state.initPaymentTypeSelect = (typeID, $el) => InitPaymentTypeSelect(state, typeID, $el);
    state.initCCInputs = $el => InitCCInputs(state, $el);
    SetupSplit(state);

    bindPaymentCollapse(state);
}

async function initPayment(state: State): Promise<void> {
    const { dom } = state;
    dom.$paymentBodyToggle?.classList.add('d-none');
    state.startLoading();
    if (state.split) {
        if (dom.$paymentBodyFieldSet) {
            dom.$paymentBodyFieldSet.disabled = false;
        }
        try {
            DrawSplit(state);
        } finally {
            state.stopLoading();
        }
        return;
    }
    state.userPaymentOptions = new Set();
    UpdatePaymentSummary(state);

    if (dom.$paymentBodyFieldSet) {
        dom.$paymentBodyFieldSet.disabled = false;
    }
    dom.$paymentBody?.replaceChildren();

    const $row = document.createElement('div');
    $row.classList.add('split-payment-inner');

    // Picking back up an in-progress (unsaved) payment.
    if (state.payment.paymentOptionTypeID && !state.payment.paymentOption) {
        PromptExistingPaymentOption(state, $row);
        dom.$paymentBody?.appendChild($row);
        state.stopLoading();
        return;
    }

    await drawPaymentCards(state, $row);
    dom.$paymentBody?.appendChild($row);

    bindCardEditDeleteButtons(state, $row);
    restoreSelection($row, state.payment);

    mountPaymentNav(state);
    bindRowRadioClicks(state);
    MaybeMountSwitch(state);

    state.stopLoading();
}

// drawPaymentCards renders one card per saved PaymentOption, paginated, and
// appends a "see more" button if there's another page. When the user has
// nothing saved, we jump straight into the new-payment prompt.
async function drawPaymentCards(state: State, $row: HTMLDivElement): Promise<void> {
    let page = 0;
    // rendered tracks ids already in $row across pages, so a duplicate id
    // from any source — server bug, edit-mode create+delete pair appearing
    // on consecutive pages, double-bound click handlers — never re-renders
    // a card the user is already looking at.
    const rendered = new Set<string>();
    const addMore = async (): Promise<void> => {
        const { paymentOptions, nextPage } = await fetchPage(state, page);

        if (!paymentOptions.length && !rendered.size) {
            PromptNewPaymentOption(state, $row);
            return;
        }

        // Draw every card before returning so initPayment's
        // bindCardEditDeleteButtons sees the fully-populated DOM.
        for (const upo of paymentOptions) {
            if (!upo.id || rendered.has(upo.id)) {
                continue;
            }
            rendered.add(upo.id);
            state.userPaymentOptions.add(upo);
            renderPaymentCard(upo, $row);
        }

        if (page === 0 && nextPage) {
            const $seeMore = template.Clone('#payment-option-see-more');
            const $seeMoreBtn = $seeMore.querySelector('button');
            $seeMoreBtn?.addEventListener('click', async () => {
                if ($seeMoreBtn.disabled) {
                    return;
                }
                $seeMoreBtn.disabled = true;
                try {
                    page++;
                    await addMore();
                } finally {
                    $seeMoreBtn.disabled = false;
                }
            });
            setTimeout(() => $row.appendChild($seeMore));
        }
        if (!nextPage) {
            $row.querySelector('#payment-option-see-more-parent')?.remove();
        }
        $row.querySelector<HTMLButtonElement>('#payment-new')?.
            addEventListener('click', () => PromptNewPaymentOption(state, $row));
    };

    await addMore();
}

// fetchPage abstracts the two sources of saved payment options — signed-in
// users hit /payment-options; guests see only in-session options.
async function fetchPage(state: State, page: number) {
    if (state.sess?.user.id) {
        const resp = await PaymentOptions(page);
        // A staged card is not on the server yet; keep it selectable beside the saved ones.
        const staged = state.payment.paymentOption;
        if (page === 0 && staged?.id === PendingPaymentOptionID && state.payment.pendingPaymentOptionCreate) {
            resp.paymentOptions = [new models.HyejooPaymentOption(staged), ...resp.paymentOptions];
        }
        return resp;
    }
    // Guests never have more than what's in state.payment.paymentOption; one
    // page covers everything.
    const guest = new models.PaymentOptionsHyejooResponse();
    if (state.payment.paymentOption && (
        state.payment.paymentOption.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK ||
        state.payment.paymentOption.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT
    )) {
        guest.paymentOptions = [new models.HyejooPaymentOption(state.payment.paymentOption)];
    }
    return guest;
}

function renderPaymentCard(upo: models.PaymentOption, $row: HTMLElement): void {
    if (upo.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK) {
        $row.appendChild(renderBankCard(upo));
    } else if (upo.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT) {
        $row.appendChild(renderCreditCard(upo));
    }
}

function renderBankCard(upo: models.PaymentOption): DocumentFragment {
    const $el = template.Clone('#payment-option-card-bank');
    wireCardRadio($el, upo);
    const $bankType = $el.querySelector('.bank-acct-type') as HTMLElement;
    if (upo.bankType && upo.accountType) {
        $bankType.textContent = `${CapitalizeFirstLetter(upo.bankType)} ${CapitalizeFirstLetter(upo.accountType)} Account`;
        if (upo.creditCardLast4) {
            $bankType.textContent += ` (...${upo.creditCardLast4})`;
        }
    }
    const $address = $el.querySelector('.bank-address') as HTMLElement;
    $address.textContent = upo.address.toLocalityLine();
    return $el;
}

function renderCreditCard(upo: models.PaymentOption): DocumentFragment {
    const $el = template.Clone('#payment-option-card-cc');
    wireCardRadio($el, upo);
    const $address = $el.querySelector('.cc-address') as HTMLElement;
    $address.textContent = upo.address.toLocalityLine();
    const $lastFour = $el.querySelector('.cc-last-four') as HTMLElement;
    if (upo.creditCardLast4) {
        $lastFour.textContent = upo.creditCardLast4.toString().padStart(4, '0');
    }
    if (upo.creditCardFirst6) {
        ApplyCardIssuer($el, upo.creditCardFirst6.toString());
    }
    return $el;
}

// wireCardRadio gives each card's radio input a unique id tied to both the
// payment option and this checkout so the form submits correctly.
function wireCardRadio($el: DocumentFragment, upo: models.PaymentOption): void {
    const $input = $el.querySelector<HTMLInputElement>('input');
    const $label = $el.querySelector<HTMLLabelElement>('label');
    if (!$input || !$label || !upo.id) {
        return;
    }
    const radioGroup = 'payment';
    $input.value = upo.id;
    $input.id = `${radioGroup}-${upo.id}`;
    $input.name = radioGroup;
    $label.htmlFor = `${radioGroup}-${upo.id}`;
}

// bindCardEditDeleteButtons wires edit/delete via a single delegated click
// listener on $row. Cards added later by See More inherit the binding for
// free — no per-card listener bookkeeping or double-bind guards needed.
function bindCardEditDeleteButtons(state: State, $row: HTMLElement): void {
    $row.addEventListener('click', e => {
        const $btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>('.payment-option-edit, .payment-option-delete');
        if (!$btn) {
            return;
        }
        const id = $btn.closest('.card-body:has(input)')?.querySelector<HTMLInputElement>('input')?.value;
        if (!id) {
            return;
        }
        const upo = Array.from(state.userPaymentOptions).find(u => u.id === id);
        if (!upo?.id) {
            return;
        }
        if ($btn.classList.contains('payment-option-edit')) {
            EditSavedPaymentOption(state, upo.id);
        } else {
            void DeleteSavedPaymentOption(state, upo.id);
        }
    });
}

// restoreSelection re-checks the radio that corresponds to the currently
// selected saved payment option, or re-fills the PO number input for an
// in-progress purchase order.
function restoreSelection($row: HTMLElement, payment: Payment): void {
    switch (payment.paymentOption?.paymentOptionTypeID) {
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK:
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT:
        setTimeout(() => {
            const $select = $row.querySelector<HTMLInputElement>(`input[value="${payment.paymentOption?.id || ''}"]`);
            if ($select) {
                $select.checked = true;
            }
        });
        break;
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO:
        setTimeout(() => {
            const $po = $row.querySelector<HTMLInputElement>('.po-num');
            if ($po) {
                $po.value = payment.purchaseOrder?.purchaseOrderNumber || '';
            }
        });
        break;
    }
}

// mountPaymentNav inserts the "Continue" nav under the list of saved
// options, unless the user is in the middle of entering a new option
// (which has its own nav).
function mountPaymentNav(state: State): void {
    const { dom } = state;
    if (dom.$paymentBody?.querySelector('.payment-options-nav')) {
        return;
    }
    const inProgress = dom.$paymentBody?.querySelector('.new-payment-option') || dom.$paymentBody?.querySelector('.new-payment-option-select');
    if (inProgress) {
        return;
    }

    dom.$paymentBody?.appendChild(template.Clone('#payment-options-nav-template'));
    dom.$paymentBody?.querySelector<HTMLButtonElement>('#payment-new')?.addEventListener('click', () => {
        // The split switch is prepended, so the first child is the switch. The saved cards live in .split-payment-inner.
        const saved = dom.$paymentBody?.querySelector<HTMLElement>('.split-payment-inner');
        PromptNewPaymentOption(state, saved ?? undefined);
    });
    dom.$paymentBody?.querySelector<HTMLButtonElement>('#payment-option-confirm')?.
        addEventListener('click', () => handleConfirmSavedSelection(state));
}

async function handleConfirmSavedSelection(state: State): Promise<void> {
    const { dom } = state;
    if (dom.$paymentBodyForm && !ReportValidity(dom.$paymentBodyForm)) {
        return;
    }
    dom.$paymentBody?.querySelectorAll<HTMLInputElement>('input[type=radio]').
        forEach($input => $input.dispatchEvent(new Event('clearinvalid')));
    const $selected = dom.$paymentBody?.querySelector<HTMLInputElement>('.payment-option input[type="radio"]:checked');
    const id = $selected?.value;
    if (!id) {
        return;
    }

    if (dom.$paymentBodyFieldSet) {
        dom.$paymentBodyFieldSet.disabled = true;
    }
    state.startLoading();

    applySelectedOption(state, id);

    if (dom.$paymentBodyFieldSet) {
        dom.$paymentBodyFieldSet.disabled = false;
    }
    UpdatePaymentSummary(state);
    await state.updateTotals().
        then(() => UpdatePaymentSummary(state)).
        then(() => state.updateOrderSummary()).
        catch(ShowError).
        finally(() => {
            state.stopLoading();
            state.paymentStepComplete = true;
            updatePaymentEditLink(state);
            // Resumed order ⇒ additional details already on the order; section hidden.
            if (state.orderResp) {
                dom.$authorizePaymentBtn.disabled = false;
            } else {
                dom.$additionalBodyToggle?.click();
            }
        });
}

// applySelectedOption adopts a saved PaymentOption onto state.payment and
// clears any stale type-specific fields (e.g. a leftover creditCard when
// the user picks a bank account).
function applySelectedOption(state: State, id: string): void {
    const upo = Array.from(state.userPaymentOptions).find(u => u.id === id);
    if (!upo) {
        return;
    }
    state.payment.paymentOption = upo;
    state.payment.paymentOptionTypeID = upo.paymentOptionTypeID;
    // Picking a saved option abandons any staged (unpersisted) card — left
    // set, submit's PersistPendingPaymentOption would recreate it and charge
    // the staged card instead of the one the customer just selected.
    if (upo.id !== PendingPaymentOptionID) {
        delete state.payment.pendingPaymentOptionCreate;
    }
    if (upo.paymentOptionTypeID !== models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO) {
        state.payment.purchaseOrder = undefined;
    }
    if (upo.paymentOptionTypeID !== models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT) {
        state.payment.creditCard = undefined;
    }
}

function bindRowRadioClicks(state: State): void {
    state.dom.$paymentBody?.querySelectorAll<HTMLDivElement>('.split-payment-inner').forEach($row => {
        $row.addEventListener('click', () => handleRowClick(state, $row));
    });
}

function handleRowClick(state: State, $row: HTMLElement): void {
    $row.querySelectorAll<HTMLInputElement>('input[type=radio]').
        forEach($input => $input.dispatchEvent(new Event('clearinvalid')));
    const $selected = $row.querySelector<HTMLInputElement>('.payment-option input[type="radio"]:checked');
    const id = $selected?.value;
    if (!id) {
        return;
    }
    applySelectedOption(state, id);
    applyAuthorizeTextForSelectedType(state);
    UpdateSmartPayWarning(state);
}

function applyAuthorizeTextForSelectedType(state: State): void {
    const { dom, payment } = state;
    [dom.$authorizePaymentText, dom.$authorizePaymentTextPlaceOrder, dom.$authorizePaymentTextPayPal].
        forEach($ => $.classList.add('d-none'));
    switch (payment.paymentOption?.paymentOptionTypeID) {
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL:
        dom.$authorizePaymentTextPayPal.classList.remove('d-none');
        break;
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CHECK:
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_WIRE:
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO:
        dom.$authorizePaymentTextPlaceOrder.classList.remove('d-none');
        break;
    default:
        dom.$authorizePaymentText.classList.remove('d-none');
        break;
    }
}

function bindPaymentCollapse(state: State): void {
    const { dom } = state;
    dom.$paymentBody?.addEventListener('shown.bs.collapse', () => {
        dom.$paymentBodyToggle?.classList.add('d-none');
    });
    dom.$paymentBody?.addEventListener('hidden.bs.collapse', () => updatePaymentEditLink(state));

    dom.$paymentBodyToggle?.addEventListener('click', () => {
        setTimeout(() => {
            if (!dom.$paymentBodyToggle?.classList.contains('collapsed')) {
                void initPayment(state);
                setTimeout(() => {
                    dom.$paymentBody?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 300);
            }
        });
        UpdatePaymentSummary(state);
        void state.updateTotals();
        void state.updateOrderSummary();
    });
}

function updatePaymentEditLink(state: State): void {
    const { dom } = state;
    const bodyOpen = dom.$paymentBody?.classList.contains('show') || dom.$paymentBody?.classList.contains('collapsing');
    dom.$paymentBodyToggle?.classList.toggle('d-none', !state.paymentStepComplete || Boolean(bodyOpen));
}
